import { Component, ViewChild } from "@angular/core";
import { IonContent, NavParams } from "@ionic/angular";
import { ChatProvider } from "src/providers/chat.provider";
import { TabsProvider } from "src/providers/tabs/tabs.provider";
import { TranslatorProvider } from "src/providers/translator.provider";
import { ChatItemModel, ChatItemStyle, ChatItemType } from "src/shared/models/chat/chat-item.model";
import { ChatType } from "src/shared/models/chat/chat.model";

@Component({
  selector: 'rota-age-chat',
  styleUrl: 'chat.page.scss',
  templateUrl: 'chat.page.html',
  standalone: false,
})
export class ChatPage {
  @ViewChild(IonContent) chatContainer: IonContent;

  id: string;
  chatTitle: string;
  type: ChatType;
  items: Array<ChatItemModel>;

  isAnimatedLastMessage = false;
  countChatUpdate = 0;

  private readonly cornerClasses = {
    single: 'single-corner',
    start: 'start-corner',
    middle: 'middle-corner',
    end: 'end-corner',
  }

  get locale() {
    return this.translator.currentLanguage;
  }

  constructor(
    private params: NavParams,
    private tabsProvider: TabsProvider,
    private chatService: ChatProvider,
    private translator: TranslatorProvider,
  ) { }

  async goBack() {
    await this.tabsProvider.forwardBack();
  }

  get chatType(): typeof ChatType {
    return ChatType;
  }

  get chatItemType(): typeof ChatItemType {
    return ChatItemType;
  }

  async ionViewWillEnter() {
    this.id = this.params.get(ChatProvider.MODEL.ID);
    this.chatTitle = this.params.get(ChatProvider.MODEL.TITLE);
    this.type = this.params.get(ChatProvider.MODEL.TYPE);

    this.chatService.items$.subscribe(items => {
      this.items = this.populateDateDelimiter(items);

      if (this.countChatUpdate == 1) this.isAnimatedLastMessage = true;
      this.countChatUpdate++;

      setTimeout(async () => {
        await this.chatContainer.scrollToBottom(1000);
      }, 1000);
    });
  }

  getCorner(currIndex: number): string {
    const prevIndex = currIndex - 1;
    const nextIndex = currIndex + 1;
    const isEqualClient = (index1: number, index2: number) => {
      return this.items[index1].fromClient === this.items[index2].fromClient;
    }
    const isNotEqualClient = (index1: number, index2: number) => {
      return this.items[index1].fromClient !== this.items[index2].fromClient;
    }
    const isEqualDay = (index1: number, index2: number) => {
      const date1 = new Date(this.items[index1].createdAt!);
      const date2 = new Date(this.items[index2].createdAt!);

      return date1.getDate() === date2.getDate()
        && date1.getMonth() === date2.getMonth()
        && date1.getFullYear() === date2.getFullYear();
    }

    if (nextIndex === this.items.length && currIndex === 0) return this.cornerClasses.single;

    if (currIndex === 0) {
      if (isEqualClient(currIndex, nextIndex)) {
        if (isEqualDay(currIndex, nextIndex)) return this.cornerClasses.start;
        return this.cornerClasses.single;
      }

      return this.cornerClasses.single;
    }

    if (nextIndex === this.items.length) {
      if (isEqualClient(currIndex, prevIndex)) {
        if (isEqualDay(currIndex, prevIndex)) return this.cornerClasses.end;

        return this.cornerClasses.single;
      }

      return this.cornerClasses.single;
    }

    if (isNotEqualClient(currIndex, prevIndex) && isNotEqualClient(currIndex, nextIndex)) {
      return this.cornerClasses.single;
    }

    if (isNotEqualClient(currIndex, prevIndex)) {
      if (isEqualDay(currIndex, nextIndex)) return this.cornerClasses.start;

      return this.cornerClasses.single;
    }

    if (isNotEqualClient(currIndex, nextIndex)) {
      if (isEqualDay(currIndex, prevIndex)) return this.cornerClasses.end;

      return this.cornerClasses.single;
    }

    if (isEqualDay(currIndex, nextIndex)) {
      if (isEqualDay(currIndex, prevIndex)) return this.cornerClasses.middle;

      return this.cornerClasses.start;
    }

    return this.cornerClasses.end;
  }

  getTitleColor(style?: ChatItemStyle) {
    if (style === undefined) return '';
    return ChatItemStyle[style].toLowerCase();
  }

  isOffset(currIndex: number): boolean {
    return !this.items[currIndex].dateDelimiter;
  }

  populateDateDelimiter(items: Array<ChatItemModel>): Array<ChatItemModel> {
    for (let index = 0; index < items.length; index++) {
      const currElement = items[index];

      if (index == 0) {
        currElement.dateDelimiter = true;
        continue;
      }

      const currDate = new Date(currElement.createdAt!);
      const prevDate = new Date(items[index - 1].createdAt!);

      currElement.dateDelimiter = index > 0 && (
        currDate.getDate() !== prevDate.getDate() ||
        currDate.getMonth() !== prevDate.getMonth() ||
        currDate.getFullYear() !== prevDate.getFullYear()
      );
    }

    return items;
  }
}